import React, { useMemo, useState } from "react";
import type { CalendarEvent } from "../../types/types";
import { MonthYearPicker } from "./month-Year-Picker";

// Calendar grid design inspired by key_event_annotation_interactive_mock_react.jsx
// Uses Tailwind classes from your index.css for colors, borders, and spacing

interface CalendarEventsProps {
  events: CalendarEvent[];
  selectedDate: Date | undefined;
  onDateSelect: (date: Date | undefined) => void;
  highlightedDates?: Date[];
}

function buildCalendarGrid(year: number, month: number) {
  const DAY = 24 * 60 * 60 * 1000;
  const first = new Date(Date.UTC(year, month, 1));
  const firstDay = first.getUTCDay();
  const weeks: { days: { date: number; ts: number; inMonth: boolean }[] }[] = [];
  let current = new Date(Date.UTC(year, month, 1 - firstDay));
  for (let w = 0; w < 6; w++) {
    const week = { days: [] as { date: number; ts: number; inMonth: boolean }[] };
    for (let d = 0; d < 7; d++) {
      const inMonth = current.getUTCMonth() === month;
      week.days.push({ date: current.getUTCDate(), ts: current.getTime(), inMonth });
      current = new Date(current.getTime() + DAY);
    }
    weeks.push(week);
  }
  return { month, year, weeks };
}

function isDateInEvent(ts: number, evt: CalendarEvent) {
  const s = new Date(evt.startAt).getTime();
  const e = new Date(evt.endAt).getTime();
  const day = new Date(ts);
  const dayStart = Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate());
  const dayEnd = dayStart + 24 * 60 * 60 * 1000 - 1;
  return dayStart <= e && dayEnd >= s;
}

function isToday(ts: number) {
  const today = new Date();
  const date = new Date(ts);
  return (
    today.getFullYear() === date.getUTCFullYear() &&
    today.getMonth() === date.getUTCMonth() &&
    today.getDate() === date.getUTCDate()
  );
}

function isHighlighted(ts: number, highlightedDates: Date[]) {
  const date = new Date(ts);
  return highlightedDates.some(
    (hDate) =>
      hDate.getFullYear() === date.getUTCFullYear() &&
      hDate.getMonth() === date.getUTCMonth() &&
      hDate.getDate() === date.getUTCDate()
  );
}

export const BigCalendar: React.FC<CalendarEventsProps> = ({
  events,
  selectedDate,
  onDateSelect,
  highlightedDates = [],
}) => {
  // Use state for displayed month/year
  const today = new Date();
  const [displayYear, setDisplayYear] = useState(selectedDate?.getFullYear() ?? today.getFullYear());
  const [displayMonth, setDisplayMonth] = useState(selectedDate?.getMonth() ?? today.getMonth());
  const [_hoveredDate, setHoveredDate] = useState<number | null>(null);

  // Update displayed month/year when selectedDate changes
  React.useEffect(() => {
    if (selectedDate) {
      setDisplayYear(selectedDate.getFullYear());
      setDisplayMonth(selectedDate.getMonth());
    }
  }, [selectedDate]);

  // Handler for picker
  function handleMonthYearChange(date: Date | undefined) {
    if (date) {
      setDisplayYear(date.getFullYear());
      setDisplayMonth(date.getMonth());
      // Optionally update selectedDate to the first of the month
      onDateSelect(date);
    }
  }

  const cal = useMemo(() => buildCalendarGrid(displayYear, displayMonth), [displayYear, displayMonth]);

  function handlePrevMonth() {
    if (displayMonth === 0) {
      setDisplayYear(displayYear - 1);
      setDisplayMonth(11);
      onDateSelect(new Date(displayYear - 1, 11, 1));
    } else {
      setDisplayMonth(displayMonth - 1);
      onDateSelect(new Date(displayYear, displayMonth - 1, 1));
    }
  }

  function handleNextMonth() {
    if (displayMonth === 11) {
      setDisplayYear(displayYear + 1);
      setDisplayMonth(0);
      onDateSelect(new Date(displayYear + 1, 0, 1));
    } else {
      setDisplayMonth(displayMonth + 1);
      onDateSelect(new Date(displayYear, displayMonth + 1, 1));
    }
  }

  const getEventColor = (source: string) => {
    switch (source) {
      case "manual":
        return "var(--chart-3)";
      case "meta":
        return "var(--chart-1)";
      default:
        return "var(--chart-5)";
    }
  };

  return (
    <div className="w-full">
      {/* Enhanced Header with better navigation */}
      <div className="mb-6">
        <div className="flex justify-center items-center gap-4">
          <button
            className="p-2 rounded-full text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-all duration-200 hover:scale-105 active:scale-95"
            onClick={handlePrevMonth}
            type="button"
            aria-label="Previous month"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <MonthYearPicker
            selected={new Date(displayYear, displayMonth, 1)}
            onChange={handleMonthYearChange}
            className="mx-4"
          />
          <button
            className="p-2 rounded-full text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-all duration-200 hover:scale-105 active:scale-95"
            onClick={handleNextMonth}
            type="button"
            aria-label="Next month"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Enhanced Calendar Grid */}
      <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
        {/* Day Headers */}
        <div className="grid grid-cols-7 bg-muted/30 border-b">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="text-center py-3 text-sm font-medium text-muted-foreground">
              {d}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {cal.weeks.flatMap((week, wi) =>
            week.days.map((day, di) => {
              const dayEvents = events.filter((e) => isDateInEvent(day.ts, e));
              const isSelected =
                selectedDate &&
                new Date(day.ts).toDateString() === selectedDate.toDateString();
              const isDayToday = isToday(day.ts);
              const isHighlightedDay = isHighlighted(day.ts, highlightedDates);
              /* const isHovered = hoveredDate === day.ts; */

              return (
                <button
                  key={`${wi}-${di}`}
                  className={`
                    relative min-h-[100px] p-3 transition-all duration-200
                    border-r border-b border-t border-l
                    ${day.inMonth ? "bg-background text-foreground" : "bg-muted/50 text-muted-foreground"}
                    ${isSelected ? "bg-primary/10 border-primary shadow-lg" : ""}
                    ${isDayToday && !isSelected ? "bg-accent/50" : ""}
                    ${isHighlightedDay ? "ring-2 ring-warning/50" : ""}
                    hover:bg-accent/70
                    focus:outline-none focus:ring-0
                  `}
                  style={{
                    borderColor: isSelected ? "var(--primary)" : "var(--border)",
                  }}
                  onClick={() => onDateSelect(new Date(day.ts))}
                  onMouseEnter={() => setHoveredDate(null)}
                  onMouseLeave={() => setHoveredDate(null)}
                  type="button"
                  aria-label={`${day.date} ${dayEvents.length > 0 ? `with ${dayEvents.length} events` : ''}`}
                >
                  {/* Date Number with enhanced styling */}
                  <div className={`
                    text-sm font-semibold mb-2 flex items-center justify-between
                    ${isDayToday ? "text-primary" : ""}
                  `}>
                    <span className={`
                      ${isDayToday ? "bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs" : ""}
                      ${isSelected && !isDayToday ? "text-primary font-bold" : ""}
                    `}>
                      {day.date}
                    </span>
                    {dayEvents.length > 0 && (
                      <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full">
                        {dayEvents.length}
                      </span>
                    )}
                  </div>

                  {/* Events Display */}
                  <div className="flex flex-col gap-1">
                    {dayEvents.slice(0, 3).map((e, idx) => (
                      <div
                        key={e.id}
                        className={`
                          text-left text-xs rounded-md px-2 py-1 transition-all duration-200
                        `}
                        style={{
                          backgroundColor: "var(--muted)",
                          color: "var(--foreground)",
                          borderLeft: `3px solid ${getEventColor(e.source)}`,
                          animationDelay: `${idx * 50}ms`,
                        }}
                      >
                        <div className="flex items-center gap-1.5">
                          <span
                            className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                            style={{
                              backgroundColor: getEventColor(e.source),
                            }}
                          />
                          <span className="truncate font-medium">{e.title}</span>
                        </div>
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-[10px] text-muted-foreground bg-muted/50 rounded px-2 py-1 text-center">
                        +{dayEvents.length - 3} more
                      </div>
                    )}
                  </div>

                  {/* Selection indicator */}
                  {isSelected && (
                    <div className="absolute inset-0 border-2 border-primary pointer-events-none opacity-50 z-10"></div>
                  )}

                  {/* Today indicator */}
                  {isDayToday && !isSelected && (
                    <div className="absolute bottom-1 right-1 w-2 h-2 bg-primary rounded-full"></div>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Enhanced Legend/Summary */}
      {highlightedDates.length > 0 && (
        <div className="mt-6 p-4 bg-muted/30 rounded-lg">
          <h3 className="text-sm font-semibold mb-2 text-muted-foreground">Highlighted Dates</h3>
          <div className="flex flex-wrap gap-2">
            {highlightedDates.slice(0, 5).map((date) => (
              <span
                key={date.toISOString()}
                className="inline-flex items-center gap-1 px-2 py-1 bg-warning/20 text-warning-foreground rounded-md text-xs"
              >
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {date.toDateString()}
              </span>
            ))}
            {highlightedDates.length > 5 && (
              <span className="text-xs text-muted-foreground">
                +{highlightedDates.length - 5} more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};